import { ExternalLink } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { fetchJson } from "../../api/rest";
import type { SocialMetrics } from "../../types/flows";
import { ErrorBanner, fmtDate, fmtNumber, kr, Panel } from "./ui";

// Every number here is a join outward from `tracking_sessions.utm_*`, so the
// attribution window is the session. Until the storefront has recorded its
// first campaign-tagged session there is genuinely nothing — which is a
// different thing from zero traffic, and the empty state says so rather than
// drawing a flat line at zero.

const SOURCES = [
	{ id: "facebook", label: "Facebook", colour: "#1877f2" },
	{ id: "instagram", label: "Instagram", colour: "#e1306c" },
	{ id: "linkedin", label: "LinkedIn", colour: "#0a66c2" },
	{ id: "newsletter", label: "Newsletter", colour: "#7367f0" },
];

const axis = { tick: { fontSize: 11 }, stroke: "#94a3b8" };

export const SocialTraffic = ({ days }: { days: number }) => {
	const [data, setData] = useState<SocialMetrics | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setError(null);
		fetchJson<SocialMetrics>(`/metrics/social?days=${days}`)
			.then(setData)
			.catch((e) => setError(String(e)));
	}, [days]);

	// The API returns one row per (day, source); a stacked chart wants one row
	// per day with a column per source.
	const chart = useMemo(() => {
		const byDate = new Map<string, Record<string, number | string>>();
		for (const row of data?.daily ?? []) {
			const existing = byDate.get(row.date) ?? {
				date: row.date,
				label: fmtDate(`${row.date}T00:00:00`),
			};
			existing[row.source] = row.sessions;
			byDate.set(row.date, existing);
		}
		return [...byDate.values()].sort((a, b) =>
			String(a.date).localeCompare(String(b.date)),
		);
	}, [data]);

	const totalsBySource = useMemo(
		() => new Map((data?.totals ?? []).map((t) => [t.source, t])),
		[data],
	);

	if (error) {
		return (
			<Panel title="Social & newsletter traffic">
				<ErrorBanner error={error} />
			</Panel>
		);
	}

	if (!data) {
		return (
			<Panel title="Social & newsletter traffic">
				<p className="text-[13px] text-slate-400">Loading…</p>
			</Panel>
		);
	}

	if (data.noDataYet) {
		return (
			<Panel
				title="Social & newsletter traffic"
				hint="what a flow's posts and campaigns brought in"
			>
				<div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-5 py-8 text-center">
					<p className="text-sm font-medium text-slate-600">
						No campaign-tagged sessions have been recorded yet.
					</p>
					<p className="mx-auto mt-1 max-w-xl text-[12px] leading-relaxed text-slate-500">
						Every link a Meta or Newsletter node sends out carries UTM
						parameters, and the storefront records them on the visitor's
						session. Nothing appears here until a flow has posted and someone
						has clicked — an empty panel means no tagged traffic has arrived,
						not that traffic was zero.
					</p>
				</div>
			</Panel>
		);
	}

	return (
		<Panel
			title="Social & newsletter traffic"
			hint={`last ${data.days} days · attributed per session`}
		>
			<div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
				{SOURCES.map((source) => {
					const totals = totalsBySource.get(source.id);
					return (
						<div
							key={source.id}
							className="rounded-xl border border-slate-200 p-4"
							style={{ borderTopColor: source.colour, borderTopWidth: 3 }}
						>
							<p className="text-[12px] font-medium text-slate-500">
								{source.label}
							</p>
							<p className="mt-1 text-xl font-bold text-slate-800">
								{fmtNumber(totals?.sessions ?? 0)}
								<span className="ml-1 text-[11px] font-normal text-slate-400">
									sessions
								</span>
							</p>
							<p className="mt-1 text-[12px] text-slate-600">
								{fmtNumber(totals?.outboundClicks ?? 0)} merchant clicks ·{" "}
								{kr(totals?.clickValue ?? 0)} qualified
							</p>
							{(totals?.linkHits ?? 0) > 0 && (
								<p
									className="mt-0.5 text-[11px] text-slate-400"
									title="Short-link hits are counted even when the session cookie never arrives, so they are the honest floor."
								>
									{fmtNumber(totals?.linkHits)} short-link hits
								</p>
							)}
						</div>
					);
				})}
			</div>

			<ResponsiveContainer width="100%" height={240}>
				<BarChart
					data={chart}
					margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
				>
					<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
					<XAxis dataKey="label" {...axis} />
					<YAxis {...axis} />
					<Tooltip />
					<Legend wrapperStyle={{ fontSize: 12 }} />
					{SOURCES.map((source) => (
						<Bar
							key={source.id}
							dataKey={source.id}
							name={source.label}
							stackId="sessions"
							fill={source.colour}
						/>
					))}
				</BarChart>
			</ResponsiveContainer>

			<div className="mt-5">
				<p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
					Top pieces
				</p>
				{data.byContent.length === 0 ? (
					<p className="text-[13px] text-slate-400">
						Nothing has been published through a flow yet.
					</p>
				) : (
					<div className="max-h-96 overflow-auto">
						<table className="w-full text-left text-[13px]">
							<thead className="sticky top-0 bg-white">
								<tr className="border-b border-slate-200 text-slate-500">
									<th className="pb-2 pr-3 font-medium">Title</th>
									<th className="pb-2 pr-3 font-medium">Channel</th>
									<th className="pb-2 pr-3 font-medium">Flow</th>
									<th className="pb-2 pr-3 font-medium">Published</th>
									<th className="pb-2 pr-3 font-medium">Sessions</th>
									<th className="pb-2 pr-3 font-medium">Outbound</th>
									<th className="pb-2 font-medium">Value</th>
								</tr>
							</thead>
							<tbody>
								{data.byContent.map((row) => (
									<tr
										key={row.utmContent}
										className="border-b border-slate-100 last:border-0"
									>
										<td className="py-2 pr-3 text-slate-700">
											{row.externalUrl ? (
												<a
													href={row.externalUrl}
													target="_blank"
													rel="noreferrer"
													className="inline-flex items-center gap-1 text-primary"
												>
													{row.title ?? row.utmContent}
													<ExternalLink className="h-3 w-3" />
												</a>
											) : (
												(row.title ?? row.utmContent)
											)}
										</td>
										<td className="py-2 pr-3 text-slate-500">
											{row.channel ?? "—"}
										</td>
										<td className="py-2 pr-3">
											{row.runId ? (
												<Link
													to={`/flows/runs/${row.runId}`}
													className="text-primary"
													title="Open the run that published this"
												>
													{row.flowName ?? `run #${row.runId}`}
												</Link>
											) : (
												<span className="text-slate-400">—</span>
											)}
										</td>
										<td className="py-2 pr-3 text-slate-500">
											{row.publishedAt ? fmtDate(row.publishedAt) : "—"}
										</td>
										<td className="py-2 pr-3 text-slate-600">
											{fmtNumber(row.sessions)}
										</td>
										<td className="py-2 pr-3 text-slate-600">
											{fmtNumber(row.outboundClicks)}
										</td>
										<td className="py-2 text-slate-600">
											{kr(row.clickValue)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</Panel>
	);
};
